import React, { useState } from 'react';
import ColorSchemeList from './ColorSchemeList';
import ColorPickerPopover from './dialogs/ColorPickerPopover';
import { Stack, Box, Button, Typography } from "@mui/material";
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import BorderOuterIcon from '@mui/icons-material/BorderOuter';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import styles from "../styles";

/**
 * DotColorPicker component allows users to pick colors for the border, fill, and text of dots.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Function} props.setColor - Callback function to set the color of the border.
 * @param {Function} props.setFillColor - Callback function to set the color of the fill.
 * @param {Function} props.setTextColor - Callback function to set the color of the text.
 * @param {string} props.xcrfToken - The CSRF token for making API requests.
 * @param {Function} props.handleSaveColorScheme - Callback function to handle saving the color scheme.
 * @param {string} props.noteColor - The current color of the border.
 * @param {string} props.noteFillColor - The current color of the fill.
 * @param {string} props.noteTextColor - The current color of the text.
 * @param {boolean} props.colorPickerVisible - Flag to indicate if the color picker for the border is visible.
 * @param {boolean} props.fillColorPickerVisible - Flag to indicate if the color picker for the fill is visible.
 * @param {boolean} props.textColorPickerVisible - Flag to indicate if the color picker for the text is visible.
 * @param {Function} props.handleBorderColorEvent - Callback function to handle the border color change event.
 * @param {Function} props.handleFillColorEvent - Callback function to handle the fill color change event.
 * @param {Function} props.handleTextColorEvent - Callback function to handle the text color change event.
 * @param {boolean} props.open - Flag to indicate if the color scheme list is open.
 * @returns {JSX.Element} DotColorPicker component.
 */
function DotColorPicker({ setColor, setFillColor, setTextColor, xcrfToken, handleSaveColorScheme, noteColor, noteFillColor, noteTextColor, colorPickerVisible, fillColorPickerVisible, textColorPickerVisible, handleBorderColorEvent, handleFillColorEvent, handleTextColorEvent, handleColorClick, handleFillColorClick, handleTextColorClick, open }) {

    const [openBorder, setOpenBorder] = useState(false);
    const [openFill, setOpenFill] = useState(false);
    const [openText, setOpenText] = useState(false);

    const handleBorderButtonClick = () => {
        setOpenBorder(!openBorder);
        handleColorClick();
    };

    const handleFillButtonClick = () => {
        setOpenFill(!openFill);
        handleFillColorClick();
    };

    const handleTextButtonClick = () => {
        setOpenText(!openText);
        handleTextColorClick();
    };

    const commonPadding = {
        paddingLeft: ".2em",
        paddingRight: ".2em",
    };

    const commonColorPickerStyle = {

        cursor: "pointer",
        display: "inline-block",

        color: "#c6c8c5",
        alignSelf: "center",
        marginRight: '1rem'
    };

    const borderColorStyle = {
        backgroundColor: noteColor,
        ...commonColorPickerStyle
    }

    const fillColorStyle = {
        backgroundColor: noteFillColor,
        ...commonColorPickerStyle
    }

    const textColorStyle = {
        backgroundColor: noteTextColor,
        ...commonColorPickerStyle
    }

    const symbolStyle = {
        color: "##1a578e",
        minWidth: "150px",
        '& .MuiFormLabel-root': {
            color: "#1a578e !important",
            borderBottomColor: "#1a578e !important",
            '& ::after': {
                borderColor: "#e5893e !important"
            },
        },
        '& .MuiInputBase-input': {
            borderColor: "#1a578e !important",
            '& ::after': {
                borderColor: "#1a578e !important"
            },
        },
        '& .MuiSelect-select': {
            color: "#1a578e",
            borderBottomColor: "#1a578e !important"
        },
        '& .MuiSvgIcon-root': {
            color: "#e51a578e893e !important",
            borderBottomColor: "#1a578e !important"
        },
        '& .MuiInputBase-root:after': {
            color: "#1a578e !important",
            borderBottomColor: "#1a578e !important"
        },
        '& .MuiInputBase-root:before': {
            color: "#1a578e !important",
            borderBottomColor: "#1a578e !important"
        },

    }

    const showNotesFormStyle = {
        color: "#1a578e",
        '& .MuiCheckbox-root': {
            color: "#1a578e !important",

        },
    }

    const typographyStyle = {
        ...commonPadding,
        color: "#1a578e",
    }

    const postNoteColorScheme = async () => {
        const noteData = {
            "type": [{ "target_id": "fretboard_colorscheme" }],
            "title": [{ "value": "Hello World" }],
            "field_note_border_color": [{ "value": noteColor }],
            "field_note_fill_color": [{ "value": noteFillColor }],
            "field_note_text_color": [{ "value": noteTextColor }]
        };
        try {
            const response = await fetch('/node?_format=json', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': xcsrf,
                },
                body: JSON.stringify(noteData),
            });
            const data = await response.json();
            return data;
        } catch (error) {
            console.error(error);
        }
    };

    const getBorderColor = (color) => {
        // Convert the color to RGB format
        const rgb = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
        const r = parseInt(rgb[1], 16);
        const g = parseInt(rgb[2], 16);
        const b = parseInt(rgb[3], 16);

        // Calculate the relative luminance of the color
        const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

        // Determine the opposite color to use as a border
        const borderColor = luminance > 0.5 ? "#000000" : "#ffffff";

        // Return a Box style object that sets the border color
        return { border: `1px solid ${borderColor}` };
    };

    const getFontColor = (color) => {
        // Convert the color to RGB format
        const rgb = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
        const r = parseInt(rgb[1], 16);
        const g = parseInt(rgb[2], 16);
        const b = parseInt(rgb[3], 16);

        // Calculate the relative luminance of the color
        const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

        // Determine the opposite color to use as a border
        const borderColor = luminance > 0.5 ? "#000000" : "#ffffff";

        // Return a Box style object that sets the border color
        return { color: `${borderColor}` };
    };

    return (
        <Box>
            <Typography variant="subtitle1" sx={typographyStyle}>Marker Color Scheme</Typography>
            <Stack direction="row" sx={styles.stackStyle}>

                <ColorSchemeList setColor={setColor} setFillColor={setFillColor} setTextColor={setTextColor} csrfToken={xcrfToken} handleSaveColorScheme={handleSaveColorScheme} open={open} />
                <Box>
                    <Button id="border-color-picker" variant="outlined" onClick={() => handleBorderButtonClick()} startIcon={<BorderOuterIcon />} size="small" sx={borderColorStyle} style={getBorderColor(noteColor)}>Border</Button>
                    {
                        colorPickerVisible && (
                            <ColorPickerPopover open={openBorder} handleButtonClick={handleBorderButtonClick} noteColor={noteColor} handleEvent={handleBorderColorEvent} elementId={"border-color-picker"} />
                        )
                    }
                </Box>
                <Box>
                    <Button id="fill-color-picker" variant="outlined" onClick={() => handleFillButtonClick()} startIcon={<FormatColorFillIcon />} size="small" sx={fillColorStyle}>Fill</Button>
                    {
                        fillColorPickerVisible && (
                            <ColorPickerPopover open={openFill} handleButtonClick={handleFillButtonClick} noteColor={noteFillColor} handleEvent={handleFillColorEvent} elementId={"fill-color-picker"} />
                        )
                    }
                </Box>
                <Box>
                    <Button id="text-color-picker" variant="outlined" onClick={() => handleTextButtonClick()} startIcon={<TextFieldsIcon />} size="small" sx={textColorStyle} style={getBorderColor(noteTextColor)}>Text</Button>
                    {
                        textColorPickerVisible && (
                            <ColorPickerPopover open={openText} handleButtonClick={handleTextButtonClick} noteColor={noteTextColor} handleEvent={handleTextColorEvent} elementId={"text-color-picker"} />
                        )
                    }
                </Box>
            </Stack>
        </Box>
    );
}

export default DotColorPicker;
